import { Chip, Table, TableContainer } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { QUERY_PROGRESS_PENDING } from "core/consts";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { getKey } from "core/model/utils/strings";
import { FileState, QueryProgress } from "core/types";
import { Checkbox as HeroUICheckbox } from "ds/ui";
import { LockSecurePin } from "ds_legacy/components/Icons/SecurePin";
import SpinnerPage from "ds_legacy/components/Spinner";
import Tooltip from "ds_legacy/components/Tooltip";
import { GREY_600, TEXT_DISABLED_COLOR } from "ds_legacy/materials/colors";
import { margin } from "ds_legacy/materials/metrics";
import { FONT_SIZE_14 } from "ds_legacy/materials/typography";
import { isFileFormUploaded } from "dsl/ecosystems/PatientFiles/utils";
import {
  EncryptedFileMessage,
  isFileHISStateful,
  isFileV2,
} from "dsl/organisms/FileTables";
import { Dispatch, MouseEvent, SetStateAction } from "react";
import styled from "styled-components";
import { useTranslations } from "translations";

const StyledTableCell = styled(TableCell)<{ disabled: boolean | undefined }>`
  border-bottom: none !important;
  font-size: ${FONT_SIZE_14};
  color: ${({ disabled }) =>
    disabled ? `${TEXT_DISABLED_COLOR} !important` : undefined};
`;

const getFileId = (file: FileState): string => {
  if (isFileHISStateful(file)) {
    return file.document_id;
  }
  return String(file.id);
};

const getFileName = (file: FileState): string => {
  if (isFileHISStateful(file)) {
    return file.document_file_name;
  }
  return (file as any).file_name?.decrypted || String(file.id);
};

const getCategory = (file: FileState): number | null => {
  if (isFileHISStateful(file)) {
    return file.document_category_number;
  }
  return (file as any).category || null;
};

const getUnavailableReason = (file: FileState): number | null => {
  if (isFileHISStateful(file)) {
    return file.unavailable_reason;
  }
  return null;
};

export function FilesTable({
  data,
  description,
  filesSelected,
  queryProgress,
  setFilesSelected,
  title,
}: {
  data: FileState[] | null;
  description: string;
  filesSelected: FileState[] | null;
  queryProgress: QueryProgress;
  setFilesSelected: Dispatch<SetStateAction<FileState[]>> | null;
  title: string;
}) {
  const getOntology = useGetOntology();
  const translations = useTranslations();

  const toggleFileSelection = (file: FileState) => {
    if (!filesSelected || !setFilesSelected) return;

    const fileId = getFileId(file);
    const selectedIndex = filesSelected.findIndex(
      (item) => getFileId(item) === fileId,
    );
    let newSelected: FileState[] = [];

    if (selectedIndex === -1) {
      newSelected = [...filesSelected, file];
    } else {
      newSelected = [
        ...filesSelected.slice(0, selectedIndex),
        ...filesSelected.slice(selectedIndex + 1),
      ];
    }

    setFilesSelected(newSelected);
  };

  const handleClick = (event: MouseEvent<unknown>, file: FileState) => {
    toggleFileSelection(file);
  };

  const isSelected = (fileId: string) => {
    if (!filesSelected) return false;
    return filesSelected.some((file) => getFileId(file) === fileId);
  };

  const getSourceText = (file: FileState): string => {
    if (isFileHISStateful(file)) {
      return translations.fileSection.fileListComponent.sourceHISInfo;
    }
    if (isFileV2(file) && file.kis_document_id_string) {
      return translations.fileSection.fileListComponent.sourceHISInfo;
    }
    if (isFileV2(file) && !file.kis_document_id_string) {
      return translations.fileSection.fileListComponent.sourceRecareInfo;
    }
    if (isFileFormUploaded(file)) {
      return translations.fileSection.fileListComponent.sourceRecareInfo;
    }
    return translations.fileSection.fileListComponent.sourceRecareInfo;
  };

  return (
    <div className="p-0">
      <h2 className="pb-3 text-heading-lg font-bold" id="tableTitle">
        {title} ({data?.length || 0})
      </h2>
      <p className="text-body-md">{description}</p>
      <EncryptedFileMessage size={FONT_SIZE_14} />

      {queryProgress === QUERY_PROGRESS_PENDING ? (
        <SpinnerPage delay={0} id="file-table-his" size="small" />
      ) : (
        <TableContainer
          sx={{
            border:
              data?.length && data.length > 0 ? "1px solid #E0E0E0" : "none",
            borderRadius: "8px",
            margin: margin(3, 0, 0, 0),
            overflow: "hidden",
            width: "100%",
          }}
        >
          <Table
            size="small"
            sx={{
              width: "100%",
            }}
          >
            <TableBody>
              {data?.map((file, index) => {
                const fileId = getFileId(file);
                const fileName = getFileName(file);
                const category = getCategory(file);
                const unavailableReason = getUnavailableReason(file);
                const disabled = !!unavailableReason;
                const isItemSelected = isSelected(fileId);
                const labelId = `file-table-checkbox-${index}`;

                return (
                  <TableRow
                    key={getKey(fileId, fileName)}
                    aria-checked={isItemSelected}
                    data-testid={
                      disabled
                        ? `unavailable-files-row-${fileId}`
                        : `available-files-row-${fileId}`
                    }
                    hover={!disabled}
                    role="checkbox"
                    selected={isItemSelected}
                    sx={{ cursor: disabled ? "auto" : "pointer" }}
                    tabIndex={-1}
                    onClick={(event) => !disabled && handleClick(event, file)}
                  >
                    {!disabled && (
                      <StyledTableCell
                        disabled={disabled}
                        padding="checkbox"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <HeroUICheckbox
                          aria-labelledby={labelId}
                          checked={isItemSelected}
                          color="primary"
                          isDisabled={disabled}
                          isSelected={isItemSelected}
                          label=""
                          size="sm"
                          onValueChange={() => toggleFileSelection(file)}
                        />
                      </StyledTableCell>
                    )}
                    <StyledTableCell
                      aria-disabled={disabled}
                      component="th"
                      disabled={disabled}
                      id={labelId}
                      scope="row"
                    >
                      {category !== null ? (
                        <Tooltip
                          title={getOntology({
                            key: category,
                            type: ONTOLOGIES.FILE_CATEGORY,
                          })}
                        >
                          <Chip
                            label={getOntology({
                              key: category,
                              type: ONTOLOGIES.FILE_CATEGORY,
                              useShortValues: true,
                            })}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      ) : (
                        <span />
                      )}
                    </StyledTableCell>
                    <StyledTableCell
                      align="left"
                      disabled={false}
                      style={{ width: "100%" }}
                    >
                      <div className="flex items-center gap-1 text-inherit">
                        <div className="flex w-full flex-col">
                          <span className="flex">{fileName}</span>
                          <span className="flex w-full text-left text-xs font-medium text-gray-600">
                            {getSourceText(file)}
                          </span>
                        </div>
                        <LockSecurePin
                          key="encrypted-badge"
                          style={{
                            color: GREY_600,
                            flexShrink: 0,
                            fontSize: FONT_SIZE_14,
                            margin: margin(0, 0, 0, 0.5),
                          }}
                          tooltip=""
                        />
                      </div>
                    </StyledTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
}
